'use client'

import { datadogLogs } from '@datadog/browser-logs'
import { useEffect, useSyncExternalStore } from 'react'
import {
  BEACON_CELL_COUNT,
  BEACON_FRACTION_CANDIDATES,
  admitDecodedT,
  beaconGeometry,
  climbProvenAfterAdmit,
  decodeBits,
  sampleCellGreens
} from './crashFrameBeacon'
import { CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'

// 8 Hz — a plain setInterval ON PURPOSE, not rAF: rAF throttles hard in
// background/occluded tabs and on low-power devices, and the beacon must keep
// its lock through that so the display re-syncs the instant the tab wakes.
const SAMPLE_INTERVAL_MS = 125

// The strip is source-cropped out of the video (FLOAT coords — §9.4) and
// resampled into a fixed 26×1-cell canvas at 8px per cell, so the centre 4×4
// window of every cell is always interior regardless of the video's size.
// Cost per sample: one drawImage + one getImageData of 208×8 px = 6,656 bytes.
const CANVAS_CELL_PX = 8
const CANVAS_W = BEACON_CELL_COUNT * CANVAS_CELL_PX
const CANVAS_H = CANVAS_CELL_PX

// A lock older than this can't chain the plausibility filter — the next
// decode is treated as a fresh lock (a new round legitimately jumps the clock
// back to negative pre-roll).
//
// ⚠️ Since the 2026-08-17 clock-skew fix this constant is ALSO the lifetime of
// the only skew-immune admission route: a chained lock skips the wall-clock
// bound, a fresh lock does not. A decode gap longer than this on a skewed
// machine demotes every later reading to the (skew-sensitive) fresh gate for
// the rest of the round — the display then rides resolveDisplayTime's
// BEACON_DEAD_MS event-clock fallback (crashHeroClamp.ts). The two constants
// are a COUPLED CONTRACT: never revert/tune one without the other — the
// ladder test in crashFrameBeacon.test.ts pins the ordering.
export const CHAIN_MAX_AGE_MS = 2000

// 3s of consecutive decode failures → beacon dead (stop sampling), with a
// cheap retry every 10s. Betting frames carry no beacon, so the sampler is
// parked dead between rounds by design; `wake()` clears it at race start.
const FAIL_DEAD_MS = 3000
const DEAD_RETRY_MS = 10_000

export type BeaconReading = Readonly<{
  /** Decoded race-relative time, SECONDS (negative during pre-roll). */
  t: number
  /** How stale the reading is — ms since it was decoded. */
  ageMs: number
}>

// ═════════════════════════════════════════════════════════════════════════
// ONE sampler for the whole app — module-level singleton, refcounted.
//
// Multiple consumers (the multiplier display's 60fps extrapolation AND the
// visual-crash gate in the dock) each mount `useFrameBeacon()`; the first
// mount starts the single 8Hz interval, the last unmount stops it. All
// consumers read the SAME lock via the module-level `getBeaconTime`, so there
// is never a second drawImage/getImageData pipeline running.
// ═════════════════════════════════════════════════════════════════════════
// `originT` = the decoded t at which this lock's CHAIN was first established
// (fresh lock). admitDecodedT uses it to tell the current round's clip (chain
// origin in pre-roll negatives) from an old round's tail that fresh-locked at
// a small positive t during betting — see its docblock.
let last: { t: number; atMs: number; originT: number } | null = null
let dead = false
let retryAt = 0
let failingSince: number | null = null
let ctx: CanvasRenderingContext2D | null = null
let intervalId: ReturnType<typeof setInterval> | null = null
let refCount = 0
// Which BEACON_FRACTION_CANDIDATES entry decoded last — tried first on the
// next tick so steady state stays one sample per tick (v49 dual-corner).
let preferredCornerIdx = 0

// ── Round scope (bug a) ──────────────────────────────────────────────────
// The beacon carries only clip-time; the ROUND it belongs to is tracked here
// so the decoder can reject readings that cannot belong to the current one.
// Without this, a new round's race_started arriving while the OLD round's
// tail is still streaming lets the old clip's frames (t≈45s) drive the new
// round's display — the "5.18x climbing over crash footage" bug.
let currentRoundId: string | null = null
// The current round's event-clock origin (ms epoch): race_started's stamp,
// moved back by 3 − R on a short-hold round (crashHold.ts) so it reads the
// beacon's clock; null while the round has no race_started yet. The
// plausibility bound is checked against it.
let roundStartTsMs: number | null = null

// Re-queried every sample (cheap at 8Hz) so element remounts — stream
// reconnects recreate the <video> — are picked up automatically. The primary
// selector targets `.main-video-container`, the literal global class
// LiveStream doubles next to its hashed CSS-module classes
// (LiveStream/index.tsx) — the only one that survives production builds.
// The fallbacks cover dev/unhashed builds.
export const findCrashStreamVideo = (): HTMLVideoElement | null =>
  document.querySelector<HTMLVideoElement>('.main-video-container video') ??
  document.querySelector<HTMLVideoElement>('.video-element') ??
  document.querySelector<HTMLVideoElement>('.dolby-feed video')

const findVideo = findCrashStreamVideo

const ensureCtx = (): CanvasRenderingContext2D | null => {
  if (!ctx) {
    const canvas = document.createElement('canvas')
    canvas.width = CANVAS_W
    canvas.height = CANVAS_H
    ctx = canvas.getContext('2d', { willReadFrequently: true })
  }
  return ctx
}

const markDead = (now: number) => {
  dead = true
  retryAt = now + DEAD_RETRY_MS
  failingSince = null
}

const onDecodeFailure = (now: number) => {
  if (failingSince === null) {
    failingSince = now
  } else if (now - failingSince >= FAIL_DEAD_MS) {
    markDead(now)
  }
}

const tick = () => {
  if (document.hidden) return // paused — nothing is being watched
  const now = performance.now()
  if (dead) {
    if (now < retryAt) return
    retryAt = now + DEAD_RETRY_MS // pace the cheap retries
  }

  const video = findVideo()
  // Missing/not-ready video is not a decode failure — just wait.
  if (!video || video.videoWidth === 0 || video.videoHeight === 0 || video.readyState < 2) {
    return
  }

  const c = ensureCtx()
  if (!c) {
    markDead(now) // no 2d context on this device — retry cheaply
    return
  }

  // v49 — TWO candidate positions: the v2 bottom-RIGHT bake and the legacy
  // bottom-LEFT (clips prepared before the move keep it until re-baked;
  // rounds alternate clips, so the corner can flip round-to-round). Try the
  // corner that decoded last FIRST — in steady state this is one sample per
  // tick, the second corner only pays on clip transitions / betting frames.
  let tCs: number | null = null
  for (let attempt = 0; attempt < BEACON_FRACTION_CANDIDATES.length; attempt++) {
    const idx = (preferredCornerIdx + attempt) % BEACON_FRACTION_CANDIDATES.length
    const fractions = BEACON_FRACTION_CANDIDATES[idx]!
    const geom = beaconGeometry(video.videoWidth, video.videoHeight, fractions)
    let data: Uint8ClampedArray
    try {
      c.drawImage(
        video,
        geom.x,
        geom.y,
        geom.count * geom.cell,
        geom.cell,
        0,
        0,
        CANVAS_W,
        CANVAS_H
      )
      data = c.getImageData(0, 0, CANVAS_W, CANVAS_H).data
    } catch {
      // SecurityError (tainted canvas — shouldn't happen on the WebRTC
      // MediaStream, but guard) or a transient draw failure: dead for the
      // session, revisited by the 10s retry / wake().
      markDead(now)
      return
    }
    tCs = decodeBits(sampleCellGreens(data, CANVAS_W, CANVAS_H, geom.count))
    if (tCs !== null) {
      preferredCornerIdx = idx
      break
    }
  }
  if (tCs === null) {
    onDecodeFailure(now)
    return
  }

  // Decode succeeded — the beacon is alive.
  dead = false
  failingSince = null

  // Per-sample admission: fresh-lock plausibility + chained continuity bounds.
  // The wall-clock branch applies only when establishing a fresh lock (clock-
  // skew incident 2026-08-17) — the full account lives in admitDecodedT.
  const eventClockT = roundStartTsMs !== null ? (Date.now() - roundStartTsMs) / 1000 : undefined

  const prevLock = last
  const chained = prevLock && now - prevLock.atMs <= CHAIN_MAX_AGE_MS ? prevLock.t : null
  const accepted = admitDecodedT(
    chained,
    tCs / 100,
    eventClockT,
    chained !== null && prevLock ? prevLock.originT : null
  )
  if (accepted === null) {
    onAdmissionRejected(now, tCs / 100, eventClockT)
    prevAdmitAtOrAboveReveal = false // a rejection breaks the consecutive-admit streak
    return // implausible — keep the last good lock, never guess
  }
  admissionRejectedSince = null

  // Frozen-stream guard: re-decoding the SAME time means the video is
  // stalled (real playback advances ≥3cs between 125ms samples). Do NOT
  // refresh the lock's age — staleness must accumulate so consumers fall
  // back to the event clock (and the crash stamp can land) instead of
  // trusting a frozen frame forever. When playback resumes, the next
  // advancing decode re-freshens the lock.
  if (prevLock && accepted === prevLock.t) return
  // A fresh lock starts a new chain and stamps its origin; a chained reading
  // carries the origin forward — admitDecodedT's tail-vs-late-race_started
  // distinction reads it.
  last = {
    t: accepted,
    atMs: now,
    originT: chained !== null && prevLock ? prevLock.originT : accepted
  }

  // Change 3 latch: two consecutive lock-UPDATING admits ≥ the reveal prove
  // the climb (frozen-stream repeats return above and never count — a stream
  // frozen on its first frame past the reveal must not arm).
  const climb = climbProvenAfterAdmit(
    prevAdmitAtOrAboveReveal,
    accepted,
    CRASH_REVEAL_DELAY_SECONDS,
    roundStartTsMs !== null
  )
  prevAdmitAtOrAboveReveal = climb.atOrAbove
  if (climb.latched) setClimbProven(true)
}

// ── Admission-rejection visibility (review 2026-08-17) ───────────────────
// A decode that SUCCEEDS but is rejected by admission leaves the sampler
// self-reporting healthy (`dead = false` runs before the admit), which is why
// the pinned-clock incident produced zero telemetry: nothing distinguished
// "video stalled" from "gate rejecting". One Datadog log per round, after the
// rejections have persisted long enough to matter, closes that hole — the
// same one-shot pattern as useVisualCrash's crash_sync_delta.
const ADMISSION_REJECT_LOG_AFTER_MS = 2000
let admissionRejectedSince: number | null = null
let admissionRejectLogged = false

const onAdmissionRejected = (now: number, rejectedT: number, eventClockT: number | undefined) => {
  if (admissionRejectedSince === null) admissionRejectedSince = now
  if (admissionRejectLogged) return
  if (now - admissionRejectedSince < ADMISSION_REJECT_LOG_AFTER_MS) return
  admissionRejectLogged = true
  datadogLogs.logger.warn('crash_beacon_admission_rejected', {
    rejectedForMs: Math.round(now - admissionRejectedSince),
    rejectedT,
    eventClockT: eventClockT ?? null,
    // eventClockT − beaconT: strongly negative suggests a slow client clock
    // (the 2026-08-17 class); large positive suggests wrong-round footage.
    deltaEstimate: eventClockT !== undefined ? Number((eventClockT - rejectedT).toFixed(3)) : null,
    lockAgeMs: last ? Math.round(now - last.atMs) : null
  })
}

// ── Climb-proven latch (Change 3: beacon-armed cash-out) ─────────────────
// Round-scoped, one-way, latched by tick() on two consecutive admitted
// decodes ≥ the reveal delay (see climbProvenAfterAdmit). Exposed via
// useSyncExternalStore because REACTIVITY IS LOAD-BEARING: on the slow-clock
// machines Change 3 targets, liveMultiplier is pinned at 1.0 and nothing else
// re-renders the betting hook when the beacon crosses the reveal — a plain
// getter would leave the cash-out button unrendered until an unrelated
// re-render.
let climbProven = false
let prevAdmitAtOrAboveReveal = false
const climbListeners = new Set<() => void>()
const notifyClimbListeners = () => climbListeners.forEach((l) => l())

export const getClimbProven = (): boolean => climbProven

export const subscribeClimbProven = (listener: () => void): (() => void) => {
  climbListeners.add(listener)
  return () => climbListeners.delete(listener)
}

const setClimbProven = (value: boolean): void => {
  if (climbProven === value) return
  climbProven = value
  notifyClimbListeners()
}

/**
 * Whether THIS round's frames have proven the climb (beacon ≥ reveal, two
 * consecutive admits). Server-truth by construction: a frame at t exists only
 * after the engine reached t, so this can never lead the backend — it is the
 * skew-immune arm of the cash-out ceiling (useCrashChipBetting).
 */
export function useBeaconClimbProven(): boolean {
  return useSyncExternalStore(subscribeClimbProven, getClimbProven, () => false)
}

/** Test-only: force the latch (and notify), as if tick() had proven it. */
export const __setClimbProvenForTests = (value: boolean): void => setClimbProven(value)

// ── Hidden-tab handling (review 2026-08-17) ─────────────────────────────
// tick() deliberately skips while hidden, but the lock's age kept counting —
// so a same-round tab-hide ≥ BEACON_DEAD_MS made the first foreground rAF
// frame take the event-clock fallback (a ~2s leap ahead of the frames that
// holdMonotonicLiveValue then pins). Hidden time is not staleness: shift the
// lock's atMs by the hidden span on restore, and re-lock immediately (plus one
// follow-up tick, in case the first drawImage still captures the pre-hide
// frame) so the fallback only ever judges VISIBLE dead time.
let hiddenSinceMs: number | null = null

const onVisibilityChange = () => {
  if (document.hidden) {
    hiddenSinceMs = performance.now()
    return
  }
  if (hiddenSinceMs !== null) {
    const now = performance.now()
    const hiddenSpan = now - hiddenSinceMs
    // Clamp to now: production telemetry (negative lockAgeMs) proved a lock
    // can be stamped AFTER the 'hidden' event (unpaired/spurious visibility
    // events), and adding the full span to it would move atMs into the
    // FUTURE — a negative age that reads as "fresh" while extrapolating the
    // display clock backward. A future atMs is never valid.
    if (last) last.atMs = Math.min(last.atMs + hiddenSpan, now)
    hiddenSinceMs = null
  }
  tick()
  setTimeout(tick, SAMPLE_INTERVAL_MS)
}

const retain = () => {
  refCount += 1
  if (intervalId === null) {
    intervalId = setInterval(tick, SAMPLE_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
  }
}

const release = () => {
  refCount -= 1
  if (refCount <= 0 && intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
    hiddenSinceMs = null
  }
}

/** Freshest lock — the frame clock of the exact frame THIS viewer sees. */
export const getBeaconTime = (): BeaconReading | null => {
  if (!last) return null
  // Floor at 0: a negative age (clock anomalies, clamped-but-racy shifts)
  // must never extrapolate the display clock backward.
  return { t: last.t, ageMs: Math.max(0, performance.now() - last.atMs) }
}

/**
 * Round-start nudge: betting frames carry no beacon, so the sampler is
 * usually parked dead when race_started fires — clear the dead flag so the
 * first climbing frames lock immediately instead of waiting out the 10s
 * retry pacing.
 */
export const wake = (): void => {
  dead = false
  retryAt = 0
  failingSince = null
}

/**
 * Round-scope the beacon (bug a). Call whenever the current round's identity
 * (race uuid) or its race_started timestamp changes — idempotent, so multiple
 * consumers (hero display, chip panel) may all drive it with the same values.
 *
 * On a round CHANGE the lock is invalidated (the old round's clip time can
 * never describe the new round) and the sampler is re-woken so the new
 * round's frames lock immediately. `startTsMs` is the current round's
 * event-clock origin (`crashEventClockOriginMs`: race_started's stamp, moved
 * back by 3 − R on a short-hold round, so the event clock reads the beacon's)
 * — undefined while the round has no race_started yet (betting), which makes
 * the plausibility gate reject any stale-tail positive reading > 5s while
 * still accepting pre-roll negatives.
 */
export const setBeaconRound = (
  nextRoundId: string | undefined,
  startTsMs: number | undefined
): void => {
  const next = nextRoundId ?? null
  if (next !== currentRoundId) {
    currentRoundId = next
    last = null // INVALIDATE — never let old-round footage drive this round
    admissionRejectedSince = null
    admissionRejectLogged = false
    prevAdmitAtOrAboveReveal = false
    setClimbProven(false) // a new round must re-prove its own climb
    wake()
  }
  roundStartTsMs = startTsMs ?? null
}

// ── The display clock's published multiplier (bug c) ────────────────────
// Written by CrashMultiplierDisplay's existing rAF (the ONE display loop —
// beacon-preferred, event-clock fallback, capped at M once the crash is
// known). Read ref-style (no React state, no extra rAF/sampler) by any UI
// that shows the player a live multiplier-derived number, so the cash-out
// bar's amount and "@ x.xx" can never contradict the hero. Consumers must
// treat a stale reading (display loop unmounted/parked) as absent and fall
// back to the event clock — see crashChipLogic.barDisplayMultiplier.
let displayMult: { value: number; atMs: number } | null = null

export type DisplayMultiplierReading = Readonly<{ value: number; ageMs: number }>

export const publishDisplayMultiplier = (value: number): void => {
  displayMult = { value, atMs: performance.now() }
}

export const getDisplayMultiplier = (): DisplayMultiplierReading | null =>
  displayMult ? { value: displayMult.value, ageMs: performance.now() - displayMult.atMs } : null

/** Test-only: drive the visibilitychange handler directly (the listener is
 * installed by retain(), which needs a mounted consumer). */
export const __visibilityChangeForTests = (): void => onVisibilityChange()

/** Test-only: reset ALL module-level sampler state between test cases. */
export const __resetBeaconForTests = (): void => {
  last = null
  admissionRejectedSince = null
  admissionRejectLogged = false
  hiddenSinceMs = null
  climbProven = false
  prevAdmitAtOrAboveReveal = false
  climbListeners.clear()
  dead = false
  retryAt = 0
  failingSince = null
  currentRoundId = null
  roundStartTsMs = null
  displayMult = null
  preferredCornerIdx = 0
}

/** Test-only: plant a decoded lock as if the sampler had just read frame-time `t`. */
export const __setBeaconLockForTests = (t: number, atMs: number = performance.now()): void => {
  last = { t, atMs, originT: t }
}

/**
 * Samples the playing crash-stream <video> at 8Hz, decodes the frame-time
 * beacon strip baked into its pixels, and exposes the freshest lock via a
 * ref-readable `getBeaconTime()` — NO React state per sample, zero renders.
 *
 * The reading is the frame clock of the exact frame THIS viewer currently
 * sees, so a consumer extrapolating `t + ageMs/1000` inside its own rAF gets
 * a display clock synced to the video, not to event arrival. Display-only:
 * money UI must stay on the backend event clock.
 *
 * The sampler itself is a refcounted module singleton — mount this hook from
 * as many consumers as needed, there is only ever ONE 8Hz pipeline.
 */
export function useFrameBeacon(): {
  getBeaconTime: () => BeaconReading | null
  wake: () => void
} {
  useEffect(() => {
    retain()
    return release
  }, [])

  return { getBeaconTime, wake }
}
